import PropTypes from 'prop-types'
import { Component, createRef, Fragment } from 'react'

import Input from 'components/common/Input'
import { InputContainer, validityCheck } from 'components/common/InputContainer'
import Icon from 'components/Icon'
import theme from 'theme'
import { omit } from 'utils/obj'

import FormSubtext from './FormSubtext'
import TextButton from './TextButton'

// We don't currently have `18` in our theme file for spacing, but it is the correct measurement
const focusedElementsOffset = 18

/**
 *
 * @typedef   {Object}   props
 * @property  {boolean}  [hasValue]         - There is a value in the input field
 * @property  {boolean}  [hideLabelOnFocus] - Hides the label instead of repositioning it on focus
 * @property  {boolean}  [isInvalid]        - Changes color to red if invalid
 * @property  {boolean}  [search]           - Adjusts positioning of text if search icon present
 */
export const InputLabel = ({
  hasValue,
  hideLabelOnFocus,
  isInvalid,
  search,
  ...rest
}) => {
  return (
    <label
      css={{
        transition: theme.transition[0],
        display: 'block',
        position: 'absolute',
        fontSize: hasValue ? theme.fontSizes[0] : theme.fontSizes[2],
        fontWeight: theme.fontWeights[0],
        paddingLeft: search ? theme.space[6] : theme.space[3],
        'input:focus + &': {
          display: hideLabelOnFocus && !hasValue ? 'none' : 'block',
          fontSize: theme.fontSizes[0],
          transform: 'translateY(-80%)',
          fontWeight: theme.fontWeights[1],
          top: theme.space[3]
        },
        top: hasValue && focusedElementsOffset,
        transform: hasValue ? 'translateY(-80%)' : 'none',
        color: theme.colors.steel[1],
        opacity: hasValue ? '0.7' : theme.opacity[0],
        ...validityCheck('isInvalid', 'red')({ isInvalid })
      }}
      {...rest}
    />
  )
}

InputLabel.propTypes = {
  hasValue: PropTypes.bool,
  hideLabelOnFocus: PropTypes.bool,
  isInvalid: PropTypes.bool,
  search: PropTypes.bool
}

const ClearIcon = ({ clearValue, ...rest }) => (
  <TextButton
    destructive
    css={{
      position: 'absolute',
      right: theme.space[3],
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 11,
      fontSize: theme.fontSizes[3]
    }}
    onClick={clearValue}
    {...rest}
  >
    <Icon css={{ display: 'block' }} icon="close" />
  </TextButton>
)

ClearIcon.propTypes = {
  clearValue: PropTypes.func
}

/**
 * @typedef   {Object}        props
 * @property  {string}        [className] - Add classes to InputContainer
 * @property  {string}        [name]      - Add name parameter, and id if absent, to field and label
 * @property  {string}        [id]        - Add id to field label, and use to associate label
 * @property  {string|number} [value]     - Value of field
 * @property  {string}        [label]     - Add label text to field label
 * @property  {boolean}       [disabled]  - Disable field
 * @property  {string}        [subtext]   - Add descriptive text under field
 * @property  {boolean}       [isValid]   - The field passed validation, changing the color to green
 * @property  {boolean}       [isInvalid] - The field failed validation, changing the color to red
 * @property  {function}      [innerRef]  - associates ref to a child element inside the field
 * @property  {boolean}       [hideLabelOnFocus]   - Hides instead of moves label on focus
 * @property  {boolean}       [clearable] - Shows clearable icon (a red `x` on the right side)
 * @property  {function}      [onChange]  - Define what to do if the value changes
 * @property  {boolean}       [nomargin]  - Removes margin from around the field
 * @property  {boolean}       [noLeftRadius]  - Removes rounded edges on the left
 * @property  {boolean}       [noRightRadius] - Removes rounded edges on the right
 * @property  {boolean}       [focusOnMount]  - Assigns focus to the input on component load
 * @property  {string}        [type]          - native type of input field (text, password, etc)
 * @property  {number}        [maxLength]     - Defines the character length of the field
 * @property  {boolean}       [search]        - Adds search icon to beginning of field
 * @property  {number}        [maxWidth]      - Defines width of the field (defaults to 100%, commonly theme.space[8] is used)
 * @property  {string}        [placeholder]   - displays placeholder, if label is not used
 */
class InputTextField extends Component {
  static defaultProps = {
    type: 'text'
  }

  constructor(props) {
    super(props)
    this.textInput = createRef()
  }

  setRef = (ref) => {
    this.textInput = ref
    this.props.innerRef && this.props.innerRef(ref)
  }

  componentDidMount() {
    if (this.textInput && this.props.focusOnMount) {
      this.textInput.focus()
    }
  }

  clearValue = () => {
    if (!this.props.onChange) return
    this.props.onChange({ target: { value: '' } })
  }

  render() {
    const {
      value,
      label,
      disabled,
      isValid,
      isInvalid,
      className,
      hideLabelOnFocus,
      clearable,
      subtext,
      nomargin: noMargin,
      noLeftRadius,
      noRightRadius,
      name,
      id,
      search,
      maxWidth,
      placeholder,
      prefix,
      ...rest
    } = this.props

    const inputProps = omit(rest, ['innerRef', 'focusOnMount'])

    const inputHasValue = value === 0 || Boolean(value)
    const hasLabel = Boolean(label)

    const unwrappedInputTextField = () => (
      <Fragment>
        <InputContainer
          className={className}
          disabled={disabled}
          isInvalid={isInvalid}
          isValid={isValid}
          maxWidth={maxWidth}
          noLeftRadius={noLeftRadius}
          noMargin={noMargin}
          noRightRadius={noRightRadius}
          subtext={subtext}
          onMouseDown={() => {
            this.textInput.focus()
          }}
        >
          {label && (
            <InputLabel
              hasValue={inputHasValue}
              hideLabelOnFocus={hideLabelOnFocus}
              htmlFor={id || name}
              isInvalid={isInvalid}
              search={search}
            >
              {label}
            </InputLabel>
          )}
          <Input
            aria-label={label ? null : placeholder}
            css={{
              transition: theme.transition[0],
              backgroundColor: 'transparent',
              border: 'none',
              '&:focus': hasLabel &&
                !hideLabelOnFocus && {
                  paddingTop: theme.space[3]
                },
              paddingTop: hasLabel && inputHasValue ? theme.space[3] : 0,
              zIndex: 10,

              '&::placeholder': {
                color: theme.colors.steel[1],
                fontWeight: theme.fontWeights[1],
                opacity: theme.opacity[0]
              }
            }}
            disabled={disabled}
            id={id || name}
            inputRef={this.setRef}
            isInvalid={isInvalid}
            name={name}
            placeholder={label ? null : placeholder}
            search={search}
            value={value}
            {...inputProps}
          />

          {inputHasValue && clearable && (
            <ClearIcon clearValue={this.clearValue} />
          )}
        </InputContainer>
        <FormSubtext
          css={{
            marginBottom: theme.space[4],
            marginTop: prefix ? -(theme.space[4] - theme.space[1]) : undefined
          }}
          isInvalid={isInvalid}
        >
          {subtext}
        </FormSubtext>
      </Fragment>
    )

    return prefix ? (
      <div css={{ display: 'grid', gridTemplateColumns: 'auto 1fr' }}>
        {prefix}
        {unwrappedInputTextField()}
      </div>
    ) : (
      unwrappedInputTextField()
    )
  }
}

InputTextField.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  disabled: PropTypes.bool,
  subtext: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isValid: PropTypes.bool,
  isInvalid: PropTypes.bool,
  innerRef: PropTypes.func,
  hideLabelOnFocus: PropTypes.bool,
  clearable: PropTypes.bool,
  onChange: PropTypes.func,
  nomargin: PropTypes.bool,
  noLeftRadius: PropTypes.bool,
  noRightRadius: PropTypes.bool,
  focusOnMount: PropTypes.bool,
  type: PropTypes.string,
  maxLength: PropTypes.number,
  search: PropTypes.bool,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  prefix: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  placeholder: PropTypes.string
}

export default InputTextField
